import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { EditorState } from "react-draft-wysiwyg";
import { useGoogleLogin } from "@react-oauth/google";

import { checkGoogleAccessAndRefreshToken, exchangeCodeForTokens, getEmailDetailsForMessageIdForGoogle, getEmailDetailsForThreadId, getEmailsForListSupplier, getRegisteredEmails, getSubscriptionId, saveAuthenticatedEmail, subscriptionRenewal } from "./redux/actions/emailSettings";
import { formatDate, formatRelativeDate } from "./services/helper";
import { toast } from "./components/CommonComponents/ToastComponent/toast";
import { WebSocketContext } from "./hooks/useWebSocket";
import { createNotification, getNotificationsUnified } from "./redux/actions/notification";
import { CONSTANTS } from "./utils/constants/constants";

export const AppContext = createContext()

const AppProvider = ({ children }) => {
    const dispatch = useDispatch()
    const store = useStore().getState()

    const { auth: { user, isLoggedIn } } = store
    const { socketIo } = useContext(WebSocketContext)

    const [userRegisteredEmails, setUserRegisteredEmails] = useState([])
    const [callerRegisteredEmails, setCallerRegisteredEmails] = useState([])
    const [unreadEmails, setUnreadEmails] = useState([])
    const [validUnReadEmails, setValidUnReadEmails] = useState([])
    const [googleParentEmails, setGoogleParentEmails] = useState([])
    const [googleChildEmails, setGoogleChildEmails] = useState([])
    const [microsoftParentEmails, setMicrosoftParentEmails] = useState([])
    const [microsoftChildEmails, setMicrosoftChildEmails] = useState([])
    const [parentEmailHistories, setParentEmailHistories] = useState([])
    const [childEmailHistories, setChildEmailHistories] = useState([])
    const [childEmailsForParentEmail, setChildEmailsForParentEmail] = useState([])
    const [allHistories, setAllHistories] = useState([])
    const [isLoadingEmailHistories, setIsLoadingEmailHistories] = useState(false)
    const [isLoadingHistories, setIsLoadingHistories] = useState(false)
    const [expandEmailHistory, setExpandEmailHistory] = useState(false)
    const [isSupplierHistoryCollapse, setIsSupplierHistoryCollapse] = useState(false)
    const [showReplyEmailBox, setShowReplyEmailBox] = useState(false)
    const [actionOnEmail, setActionOnEmail] = useState('')
    const [newMicrosoftEmails, setNewMicrosoftEmails] = useState([])
    const [unreadEmailCount, setUnreadEmailCount] = useState(0)
    const [contactsFromAddCallers, setContactsFromAddCallers] = useState([])
    const [subscription, setSubscription] = useState([]);
    const [emailConversation, setEmailConversation] = useState([])
    const [forwardConversation, setForwardConversation] = useState([])
    const [oldEmails, setOldEmails] = useState({})
    const [oldEmailDataInContext, setOldEmailDataInContext] = useState({})
    const [parentThread, setParentThread] = useState(null)
    const [inputEmailFrom, setInputEmailFrom] = useState([])
    const [inputEmailTo, setInputEmailTo] = useState([])
    const [inputEmailCc, setInputEmailCc] = useState([])
    const [inputEmailBcc, setInputEmailBcc] = useState([])
    const [subject, setSubject] = useState('')
    const [reauthenticateModal, setReauthenticateModal] = useState(false)
    const [emailsToReauthenticate, setEmailsToReauthenticate] = useState('')
    const [editorState, setEditorState] = useState(() =>
        EditorState?.createEmpty()
    );
    const [oldUnreadNotification, setOldUnreadNotification] = useState([])
    const [unReadCount, setUnReadCount] = useState(0)
    const [partnerFilter, setPartnerFilter] = useState("")
    const [headerNotificationCount, setHeaderNotificationCount] = useState(0)
    const [notificationAllCount, setNotificationAllCount] = useState(0)
    const [notificationUnreadCount, setNotificationUnreadCount] = useState(0)
    const [notificationCheckbox, setNotificationCheckbox] = useState(false)

    const clearStates = () => {
        setUnreadEmails([])
        setUserRegisteredEmails([])
        setCallerRegisteredEmails([])
        setGoogleParentEmails([])
        setGoogleChildEmails([])
        setMicrosoftParentEmails([])
        setMicrosoftChildEmails([])
        setParentEmailHistories([])
        setChildEmailHistories([])
        setChildEmailsForParentEmail([])
        setAllHistories([])
        setEmailConversation([])
        setForwardConversation([])
        setOldEmails({})
        setOldEmailDataInContext({})
        setInputEmailFrom([])
        setInputEmailTo([])
        setInputEmailCc([])
        setInputEmailBcc([])
        setNewMicrosoftEmails([])
        setContactsFromAddCallers([])
    }

    const googleLoginHandler = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            const response = await dispatch(exchangeCodeForTokens(tokenResponse?.code))
                .then((res) => res)
                .catch((err) => (err ? [] : []));
            const result = await dispatch(saveAuthenticatedEmail({
                email: response?.userInfo,
                accessToken: response.access_token,
                refreshToken: response.refresh_token,
                expires_in: response.expires_in,
                domain: 'google',
                emailSyncDuration: 'month',
                isReauthenticated: googleLoginHandler.isReauthenticated,
                userId: user?.id
            }))
                .then((res) => res)
                .catch((err) => (err ? [] : []));

            if (result?.status === 200) {
                toast.show({
                    title: 'Success',
                    description: googleLoginHandler.isReauthenticated
                        ? CONSTANTS.TOAST_MESSAGES.SUCCESS.EMAIL_LOGIN_UPDATE_SUCCESS
                        : CONSTANTS.TOAST_MESSAGES.SUCCESS.EMAIL_LOGIN_SUCCESS
                });

                if (googleLoginHandler.page === 'addCallerDetails' || googleLoginHandler.page === 'masterSupplierDetails') {
                    getEmailsForListSupHandler(googleLoginHandler.callerAssignedForEmail, googleLoginHandler.contacts, googleLoginHandler.histories, googleLoginHandler.currentTab, googleLoginHandler.tmpCallerRegisteredEmails)

                } else if (googleLoginHandler.page === 'sequence' || googleLoginHandler.page === 'tasks') {
                    googleLoginHandler.cb()
                }
            }
        },
        onError: () => {
            toast.show({
                title: 'Danger',
                description: CONSTANTS.TOAST_MESSAGES.DANGER.EMAIL_LOGIN_ERROR
            });
        },
        scope: CONSTANTS.GOOGLE.SCOPE,
        access_type: CONSTANTS.GOOGLE.ACCESS_TYPE,
        prompt: CONSTANTS.GOOGLE.PROMPT,
        flow: CONSTANTS.GOOGLE.FLOW,
        ux_mode: CONSTANTS.GOOGLE.UX_MODE,
        state: CONSTANTS.GOOGLE.STATE
    });

    const googleLogin = (isReauthenticated, callerAssignedForEmail = null, contacts = [], histories = [], currentTab = null, tmpCallerRegisteredEmails = [], page = '', cb = () => { }) => {
        googleLoginHandler.isReauthenticated = isReauthenticated;
        googleLoginHandler.callerAssignedForEmail = callerAssignedForEmail;
        googleLoginHandler.contacts = contacts;
        googleLoginHandler.histories = histories;
        googleLoginHandler.currentTab = currentTab;
        googleLoginHandler.tmpCallerRegisteredEmails = tmpCallerRegisteredEmails;
        googleLoginHandler.page = page;
        googleLoginHandler.cb = cb;

        googleLoginHandler();
    };

    const googleTokensValidationHandler = useCallback(async (callerAssignedForEmail) => {
        let registerEmails = await getRegisteredEmailsHandler(callerAssignedForEmail)
        let filterGoogleEmails = registerEmails?.filter((email) => email?.domain === 'google')
        for (let index = 0; index < filterGoogleEmails?.length; index++) {
            const googleEmail = filterGoogleEmails[index];
            let result = await dispatch(checkGoogleAccessAndRefreshToken(googleEmail?.accessToken, googleEmail?.refreshToken)).then((res) => res).catch((err) => { if (err) { return [] } })
            if (!result?.tokenDetails) {
                toast.show({
                    title: 'Warning',
                    description: CONSTANTS.TOAST_MESSAGES.WARNING.TOKENS_VALIDATION_ERROR,
                })
            } else if (Object.keys(result?.tokenDetails).length > 0 && !result?.tokenDetails?.isAccessTokenValid && !result?.tokenDetails?.isRefreshTokenValid) {
                setReauthenticateModal(true)
                setEmailsToReauthenticate(googleEmail?.email)
            }
        }
    }, [])

    const getRegisteredEmailsHandler = async (paginatedCallersIds) => {
        if (!isLoggedIn) {
            return
        }
        let registeredEmails = await dispatch(getRegisteredEmails(paginatedCallersIds)).then(res => res).catch((err) => { if (err) { return [] } })
        return registeredEmails
    }

    const getEmailDetailsForMessageIdHandler = async (accessToken, messageIds, email, refreshToken, inChunks) => {
        let result = await dispatch(getEmailDetailsForThreadId({ accessToken, messageIds, email, refreshToken, inChunks })).then(res => res).catch((err) => { if (err) { return [] } })
        return result
    }

    const categorizeEmail = (emailHistories) => {
        let googleThreads = {};
        let microsoftConversations = {};

        let parentEmails = [];
        let childEmails = [];

        // Step 1: Group emails by threadId (Google) or conversationId (Microsoft)
        emailHistories.forEach(email => {
            if (email.threadId) {
                // Google Email (Grouped by threadId)
                if (!googleThreads[email.threadId]) {
                    googleThreads[email.threadId] = [];
                }
                googleThreads[email.threadId].push(email);
            } else if (email.conversationId) {
                // Microsoft Email (Grouped by conversationId)
                if (!microsoftConversations[email.conversationId]) {
                    microsoftConversations[email.conversationId] = [];
                }
                microsoftConversations[email.conversationId].push(email);
            }
        });

        // Step 2: Process Google Threads
        Object.values(googleThreads).forEach(threadEmails => {
            threadEmails.sort((a, b) => new Date(a.sentDate) - new Date(b.sentDate)); // Sort by date
            parentEmails.push(threadEmails[0]); // First email is parent
            childEmails.push(...threadEmails.slice(1)); // Rest are children
        });

        // Step 3: Process Microsoft Conversations
        Object.values(microsoftConversations).forEach(conversationEmails => {
            conversationEmails.sort((a, b) => new Date(a.sentDate) - new Date(b.sentDate)); // Sort by date
            parentEmails.push(conversationEmails[0]); // First email is parent
            childEmails.push(...conversationEmails.slice(1)); // Rest are children
        });

        return { parentEmails, childEmails };
    };

    const getEmailsForListSupHandler = useCallback(async (id, contactsArray, histories, currentTab, tmpCallerRegisteredEmails) => {
        if (currentTab === 'all') {
            setIsLoadingHistories(true)
        } else if (currentTab === 'email') {
            setIsLoadingEmailHistories(true)
        }

        let allEmaiHistories = []
        let googleEmailWithMsgAndThrdId = []

        if (id && contactsArray?.length > 0) {
            const contactEmails = contactsArray?.map((contact) => contact?.email)?.filter((email) => email)

            if (tmpCallerRegisteredEmails?.length > 0) {
                for (let index = 0; index < tmpCallerRegisteredEmails.length; index++) {
                    const registeredEmail = tmpCallerRegisteredEmails[index];
                    for (let subIndex = 0; subIndex < contactEmails.length; subIndex++) {
                        const contactEmail = contactEmails[subIndex];
                        const emailHistory = await dispatch(getEmailsForListSupplier({ listSupplierEmails: contactEmail, callerRegisteredEmail: registeredEmail?.email, domain: registeredEmail.domain, accessToken: registeredEmail.accessToken, refreshToken: registeredEmail.refreshToken })
                        ).then((res) => res).catch((err) => { if (err) { return [] } })

                        if (emailHistory?.domain === 'google') {
                            Object?.keys(emailHistory)?.map((label) => {
                                emailHistory[label]?.messages?.map((rawEmail) => {
                                    googleEmailWithMsgAndThrdId?.push(rawEmail)
                                })
                            })
                        } else {
                            let allInternetHeaders = emailHistory?.sentApiWithInternetHeadersResult?.value ? JSON?.parse(JSON?.stringify(emailHistory?.sentApiWithInternetHeadersResult?.value)) : null
                            if (allInternetHeaders) {
                                Object?.keys(emailHistory)?.map((label) => {
                                    if (label !== 'sentApiWithInternetHeadersResult') {
                                        emailHistory[label]?.value?.map((email) => {
                                            if (label === 'sentApiResult') {
                                                let customHeader = allInternetHeaders?.filter(internetMessageHeader => internetMessageHeader?.id === email?.id)?.[0]
                                                email['customHeader'] = customHeader
                                            }
                                            allEmaiHistories?.push(email)
                                        })
                                    }
                                })
                            }

                        }
                    }

                    if (registeredEmail?.domain === 'google') {
                        for (let thirdIndex = 0; thirdIndex < googleEmailWithMsgAndThrdId.length; thirdIndex++) {
                            const rawEmail = googleEmailWithMsgAndThrdId[thirdIndex];
                            let emailDetailForGoogle = await dispatch(getEmailDetailsForMessageIdForGoogle({ accessToken: registeredEmail?.accessToken, messageId: rawEmail?.id, email: registeredEmail?.email, refreshToken: registeredEmail?.refreshToken })).then((res) => res).catch((err) => { if (err) { return [] } })
                            allEmaiHistories?.push(emailDetailForGoogle?.data)
                        }
                    }
                }
            }
        }
        const allFormatedEmailHistories = allEmaiHistories?.map((emailHistory) => convertEmailHistoryStructure(emailHistory?.historyId ? 'google' : 'microsoft', null, emailHistory, tmpCallerRegisteredEmails))
        const { parentEmails, childEmails } = categorizeEmail(allFormatedEmailHistories);

        if (currentTab === 'all') {
            sortHistories(histories, [...parentEmails])
            setIsLoadingHistories(false)
        } else if (currentTab === 'email') {
            setIsLoadingEmailHistories(false)
        }

        setParentEmailHistories(parentEmails)
        setChildEmailHistories(childEmails)
    }, [])

    const sortHistories = useCallback(async (histories, emailHistories) => {
        let allHistories = [...emailHistories, ...histories]
        let sortedAllHistories = allHistories?.sort((a, b) => {
            let tmpA = a?.hasOwnProperty('isRecorded') ? a.callStartTime : a?.createdAt ? a?.createdAt : a?.sentDate
            let tmpB = b?.hasOwnProperty('isRecorded') ? b.callStartTime : b?.createdAt ? b?.createdAt : b?.sentDate

            return new Date(tmpB) - new Date(tmpA)
        })
        setAllHistories(sortedAllHistories)
    }, [])

    const extractEmail = (input) => {
        if (!input) {
            return []
        } else if (input?.includes('<')) {
            const emailPattern = /<([^>]+)>/g;
            const emails = [];
            let match;

            while ((match = emailPattern.exec(input)) !== null) {
                emails.push(match[1]);
            }

            return emails;
        } else {
            if (input?.includes(',')) {
                let emails = input?.split(",")
                return emails
            } else {
                return [input]
            }
        }
    };

    const convertEmailHistoryStructure = (domain, emailType, emailHistory, tmpCallerRegisteredEmails) => {
        let tmpEmailStructure = {}
        if (domain === 'google') {
            tmpEmailStructure['messageId'] = emailHistory?.id
            tmpEmailStructure['customId'] = emailHistory?.customHeader?.internetMessageHeaders?.filter((header) => header?.name === "X-UUID")?.[0]?.value
            tmpEmailStructure['threadId'] = emailHistory?.threadId
            tmpEmailStructure['subject'] = emailHistory?.payload?.headers?.filter((header) => header?.name === "Subject")[0]?.value
            tmpEmailStructure['isSent'] = emailHistory?.labelIds?.includes("SENT")
            tmpEmailStructure['sentDate'] = formatDate(emailHistory?.payload?.headers?.filter((header) => header?.name === "Date")[0]?.value)
            tmpEmailStructure['receivedDate'] = formatDate(emailHistory?.payload?.headers?.filter((header) => header?.name === "Date")[0]?.value)
            tmpEmailStructure['timeSpan'] = formatRelativeDate(tmpEmailStructure?.sentDate)
            tmpEmailStructure['receiverName'] = emailHistory?.payload?.headers?.filter((header) => header?.name?.toLowerCase() === "to")[0]?.value?.split("<")[0]
            tmpEmailStructure['senderName'] = emailHistory?.payload?.headers?.filter((header) => header?.name?.toLowerCase() === "from")[0]?.value?.split("<")[0]
            tmpEmailStructure['Cc'] = extractEmail(emailHistory?.payload?.headers?.filter((header) => header?.name?.toLowerCase() === "cc")[0]?.value)
            tmpEmailStructure['Bcc'] = extractEmail(emailHistory?.payload?.headers?.filter((header) => header?.name?.toLowerCase() === "bcc")[0]?.value)
            tmpEmailStructure['body'] = extractMainContent(emailHistory?.snippet)
            tmpEmailStructure['domain'] = 'google'
            let tmpReceiver = emailHistory?.payload?.headers?.filter((header) => header?.name === "To")[0]?.value
            if (tmpReceiver?.includes('<')) {
                const emailMatch = tmpReceiver.match(/<([^>]+)>/);

                if (emailMatch) {
                    tmpReceiver = emailMatch[1]
                }
            }
            tmpEmailStructure['receiverEmail'] = tmpReceiver
            let tmpSender = emailHistory?.payload?.headers?.filter((header) => header?.name === "From")[0]?.value
            if (tmpSender?.includes('<')) {
                const emailMatch = tmpSender.match(/<([^>]+)>/);

                if (emailMatch) {
                    tmpSender = emailMatch[1]
                }
            }
            tmpEmailStructure['senderEmail'] = tmpSender
            let bodyData = emailHistory?.payload?.parts?.filter((part) => part?.mimeType === 'text/html')[0]?.body?.data
            tmpEmailStructure['mainBody'] = extractBodyContent('google', bodyData) || emailHistory?.snippet
            tmpEmailStructure['hideActionBtn'] = tmpEmailStructure?.isSent ? true : false
            tmpEmailStructure['isRead'] = emailHistory?.labelIds.includes("UNREAD") ? false : true
            tmpEmailStructure['isNotified'] = false
        } else {
            tmpEmailStructure['messageId'] = emailHistory?.id
            tmpEmailStructure['conversationId'] = emailHistory?.conversationId
            tmpEmailStructure['customId'] = emailHistory?.customHeader?.internetMessageHeaders?.filter((header) => header?.name === "X-UUID")?.[0]?.value
            tmpEmailStructure['subject'] = emailHistory?.subject
            tmpEmailStructure['timeSpan'] = formatRelativeDate(emailHistory?.sentDateTime)
            let senderAddress = emailHistory?.sender?.emailAddress?.address
            let isExist = tmpCallerRegisteredEmails?.lenghth === 0 ? [] : tmpCallerRegisteredEmails?.filter((registeredEmail) => registeredEmail?.email?.toLowerCase() === senderAddress?.toLowerCase())
            tmpEmailStructure['isSent'] = isExist?.length > 0 ? true : false
            tmpEmailStructure['sentDate'] = formatDate(emailHistory?.sentDateTime)
            tmpEmailStructure['receivedDate'] = formatDate(emailHistory?.receivedDateTime)
            tmpEmailStructure['receiverName'] = emailHistory?.toRecipients?.[0]?.emailAddress?.name
            tmpEmailStructure['senderName'] = emailHistory?.sender?.emailAddress?.name
            tmpEmailStructure['receiverEmail'] = emailHistory?.toRecipients?.[0]?.emailAddress?.address
            tmpEmailStructure['senderEmail'] = emailHistory?.sender?.emailAddress?.address
            tmpEmailStructure['body'] = extractMainContent(emailHistory?.bodyPreview)
            tmpEmailStructure['domain'] = 'microsoft'
            tmpEmailStructure['Cc'] = emailHistory?.ccRecipients?.map((email) => email?.emailAddress?.address)
            tmpEmailStructure['Bcc'] = emailHistory?.bccRecipients?.map((email) => email?.emailAddress?.address)
            tmpEmailStructure['mainBody'] = extractBodyContent('microsoft', emailHistory?.body?.content)
            tmpEmailStructure['hideActionBtn'] = tmpEmailStructure?.isSent ? true : false
            tmpEmailStructure['isRead'] = emailHistory?.isRead
            tmpEmailStructure['isNotified'] = false
        }
        return tmpEmailStructure
    }

    const extractMainContent = (body) => {
        const patterns = [
            /From:.*$/i, // Matches everything starting with 'From:' till the end
            /On [a-zA-Z]{3}, .*$/i // Matches everything starting with 'On Fri,' or similar till the end
        ];

        let result = body;
        patterns?.forEach(pattern => {
            result = result?.replace(pattern, '')?.trim();
        });

        return result;
    };

    const extractBodyContent = (domain, content) => {
        let body = '';
        if (content) {
            if (domain === 'google') {
                const normalizedContent = content
                    ?.replace(/-/g, '+')
                    ?.replace(/_/g, '/')
                    ?.padEnd(content.length + (4 - (content.length % 4)) % 4, '=');

                if (normalizedContent) {
                    const decodedHtml = atob(normalizedContent);
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(decodedHtml, 'text/html');
                    body = doc.body.innerHTML;
                }
            } else {
                const parser = new DOMParser();
                const doc = parser.parseFromString(content, 'text/html');

                body = doc.body.innerHTML;
            }
        }
        return body
    };

    const getNewUnreadNotification = useCallback(async (searchTerm = '', unReadFilterApplied = false, filter = '', active, checkedFilter = false) => {
        const unReadNotification = await dispatch(getNotificationsUnified({ offset: null, limit: null, type: 'header', filterValue: '', searchTerm: searchTerm, unReadFilterApplied, filter, checkedFilter })).then(res => res).catch(err => [])
        console.log("unReadNotification", unReadNotification)
        if (active === 'all') {
            if (filter) {
                setNotificationAllCount(unReadNotification?.allCountWithFilter)
            } else {
                setNotificationAllCount(unReadNotification?.allCount)
                setNotificationUnreadCount(unReadNotification?.unreadCount)
            }
        } else {
            if (filter) {
                setNotificationUnreadCount(unReadNotification?.unReadCountWithFilter)
            } else {
                setNotificationAllCount(unReadNotification?.allCount)
                setNotificationUnreadCount(unReadNotification?.unreadCount)
            }
        }

        setOldUnreadNotification( unReadNotification?.length === 0 ? [] : [...unReadNotification?.results] )
        setHeaderNotificationCount( unReadNotification?.length === 0 ? [] : unReadNotification?.headerUnreadCount)
    }, [])

    useEffect(() => {
        if (!socketIo) return;
        socketIo.on('email-notify', async (newEmail) => {
            if (newEmail?.newEmail?.id) {
                let allRegisteredEmails = [...callerRegisteredEmails, userRegisteredEmails]
                let formattedEmail = convertEmailHistoryStructure(newEmail?.domain, null, newEmail?.newEmail, allRegisteredEmails);
                let tmpObj = {
                    type: 'email',
                    domain: formattedEmail?.domain,
                    emailChainId: formattedEmail?.domain === 'google' ? formattedEmail?.threadId : formattedEmail?.conversationId,
                    emailId: formattedEmail?.messageId,
                    emailFrom: formattedEmail?.senderEmail,
                    emailTo: formattedEmail?.receiverEmail,
                    bodyPreview: formattedEmail?.body,
                    supplierId: null,
                    phone: null,
                    isRead: false,
                    listSupplierId: null,
                    qaRejectionNotes: null,
                    subject: formattedEmail?.subject
                }
                await dispatch(createNotification(tmpObj)).then((res) => { }).catch(err => {
                    toast.show({
                        title: 'Danger',
                        description: CONSTANTS.TOAST_MESSAGES.DANGER.NOTI_CREATE_ERROR
                    });
                })
                let isExist = [...parentEmailHistories, ...childEmailHistories]?.find(emailHistory => emailHistory?.messageId === formattedEmail?.messageId);
                let isExistInUnreadEmails = unreadEmails?.find((unreadEmail) => unreadEmail?.messageId === formattedEmail?.messageId)
                if (isExist?.messageId || isExistInUnreadEmails?.messageId) {
                    return
                } else {
                    getNewUnreadNotification()
                    toast.show({
                        title: 'Success',
                        description: CONSTANTS.TOAST_MESSAGES.SUCCESS.NEW_EMAIL
                    });
                    let tmpUnreadEmails = Array.from(new Set([...unreadEmails, formattedEmail]))
                    setUnreadEmails(tmpUnreadEmails)
                }
            }
        });

        return () => {
            socketIo.off('email-notify');
        };
    }, [socketIo]);

    const getSubscriptions = useCallback(async () => {
        let tmpSubscriptionList = []
        let regEmails = await dispatch(getRegisteredEmails([user?.id], user?.roleName === CONSTANTS.ROLE_NAME.ADMIN && user?.isCaller ? 'all' : '')).then(res => res).catch((err) => { if (err) { return [] } })
        for (let index = 0; index < regEmails?.length; index++) {
            const regEmail = regEmails[index];
            let response = await dispatch(getSubscriptionId(regEmail?.email)).then((res) => res).catch((err) => { if (err) { return [] } })
            tmpSubscriptionList.push(response?.data?.emailConnection)
        }
        setSubscription(tmpSubscriptionList)
    }, [])

    useEffect(() => {
        getSubscriptions()
        getNewUnreadNotification()
    }, []);

    useEffect(() => {
        if (!subscription || subscription.length === 0) return;

        const now = new Date();
        const timers = [];

        subscription?.forEach(sub => {
            if (sub?.inboxExpirationDateTime && sub?.junkExpirationDateTime) {
                const checkAndRenew = async (subscriptionId, expirationTime, type) => {
                    const timeUntilRenewal = new Date(expirationTime)?.getTime() + 20 * 60 * 60 * 1000 - now;
                    if (timeUntilRenewal <= 0) {
                        await renewSubscription(sub?.email, subscriptionId, type, sub?.accessToken);
                    } else {
                        const timer = setTimeout(async () => {
                            await renewSubscription(sub?.email, subscriptionId, type, sub?.accessToken,);
                        }, timeUntilRenewal);
                        timers.push(timer);
                    }
                };

                if (sub?.inboxSubscriptionId) {
                    checkAndRenew(sub?.inboxSubscriptionId, sub?.inboxExpirationDateTime, "inbox");
                }

                if (sub?.junkSubscriptionId) {
                    checkAndRenew(sub?.junkSubscriptionId, sub?.junkExpirationDateTime, "junk");
                }
            }
        });

        return () => timers.forEach(timer => clearTimeout(timer)); // Cleanup timers on unmount

    }, [subscription]);

    const renewSubscription = async (email, subscriptionId, type, accessToken) => {
        await dispatch(subscriptionRenewal(email, subscriptionId, type, accessToken))
    };

    const handleToggle = (threadId) => {
        setExpandEmailHistory(!expandEmailHistory)
        if (!expandEmailHistory) {
            let filterChildEmails = childEmailHistories?.filter((email) => email?.threadId === threadId)
            setChildEmailsForParentEmail(filterChildEmails)
        }
    };

    return (
        <AppContext.Provider value={{
            getEmailsForListSupHandler,
            clearStates,
            handleToggle,
            setIsLoadingEmailHistories,
            setIsLoadingHistories,
            setExpandEmailHistory,
            setIsSupplierHistoryCollapse,
            setShowReplyEmailBox,
            setInputEmailFrom,
            setInputEmailTo,
            setInputEmailCc,
            setInputEmailBcc,
            setSubject,
            setEditorState,
            setOldEmailDataInContext,
            setParentThread,
            setOldEmails,
            setActionOnEmail,
            setEmailConversation,
            getRegisteredEmailsHandler,
            getEmailDetailsForMessageIdHandler,
            convertEmailHistoryStructure,
            setUnreadEmails,
            setNewMicrosoftEmails,
            setUnreadEmailCount,
            setForwardConversation,
            sortHistories,
            setCallerRegisteredEmails,
            setChildEmailHistories,
            setContactsFromAddCallers,
            setParentEmailHistories,
            categorizeEmail,
            setValidUnReadEmails,
            googleLogin,
            googleTokensValidationHandler,
            setEmailsToReauthenticate,
            setReauthenticateModal,
            getSubscriptions,
            setUnReadCount,
            getNewUnreadNotification,
            setOldUnreadNotification,
            setNotificationAllCount,
            setNotificationUnreadCount,
            notificationAllCount,
            notificationUnreadCount,
            headerNotificationCount,
            oldUnreadNotification,
            unReadCount,
            validUnReadEmails,
            contactsFromAddCallers,
            childEmailHistories,
            callerRegisteredEmails,
            forwardConversation,
            unreadEmailCount,
            newMicrosoftEmails,
            unreadEmails,
            emailConversation,
            actionOnEmail,
            oldEmails,
            expandEmailHistory,
            childEmailsForParentEmail,
            parentThread,
            oldEmailDataInContext,
            editorState,
            subject,
            inputEmailFrom,
            inputEmailTo,
            inputEmailCc,
            inputEmailBcc,
            showReplyEmailBox,
            isSupplierHistoryCollapse,
            expandEmailHistory,
            isLoadingHistories,
            isLoadingEmailHistories,
            googleParentEmails,
            googleChildEmails,
            microsoftParentEmails,
            microsoftChildEmails,
            parentEmailHistories,
            childEmailHistories,
            allHistories,
            reauthenticateModal,
            emailsToReauthenticate,
            oldUnreadNotification,
            partnerFilter,
            setPartnerFilter,
            notificationCheckbox, 
            setNotificationCheckbox
        }}
        >
            {children}
        </AppContext.Provider>
    )
}

export default AppProvider